import { array, nonEmptyArray, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { isBoolean, isEmpty, isNil, isNumber } from 'lodash';
import {
    AgeCategoryClassification,
    BookingEngine,
    ConfigurationResult,
    Enterprise,
    OccupancyData,
} from '@mews-distributor/data-access';
import { keyById } from '@mews/fp';
import { DEFAULT_ADULT_COUNT, DEFAULT_CHILD_COUNT, DEFAULT_DAYS_INTERVAL } from '../../constants';

export const getPrimaryConfiguration = (configurations: BookingEngine[]) => {
    const primaryConfiguration = pipe(
        configurations,
        array.findFirst((bookingEngine) => bookingEngine.isPrimary)
    );

    /*
     * BE API does not set primary configuration (with `isPrimary`) when FE sends enterprise id instead of configuration id.
     * In that case, take the first configuration instead, we don't care about the order.
     * Properties should use the configuration id, enterprise id is deprecated. Let's support it as little as possible.
     */
    if (option.isNone(primaryConfiguration)) {
        return array.head(configurations);
    }

    return primaryConfiguration;
};

export const getConfigurationEnterprise = (configurationResult: ConfigurationResult) => {
    const services = keyById(configurationResult.services);
    const enterprises = keyById(configurationResult.enterprises);
    return (bookingEngineConfiguration: BookingEngine) =>
        pipe(
            record.lookup(bookingEngineConfiguration.serviceId, services),
            option.map((service) => service.enterpriseId),
            option.flatMap((enterpriseId) => record.lookup(enterpriseId, enterprises))
        );
};

export const getConfigurationOccupancy = (configurationResult: ConfigurationResult) => {
    const ageCategories = keyById(configurationResult.ageCategories);
    return (occupancyData: OccupancyData[]) =>
        pipe(
            occupancyData,
            array.map((occupancy) =>
                pipe(
                    ageCategories,
                    record.lookup(occupancy.ageCategoryId),
                    option.map(({ classification }) => ({ count: occupancy.personCount, classification }))
                )
            ),
            array.compact,
            nonEmptyArray.groupBy((occupancy) => occupancy.classification),
            record.map(nonEmptyArray.head),
            (occupancy) => {
                const areBothAgeCategoriesZero =
                    occupancy[AgeCategoryClassification.Adult]?.count === 0 &&
                    occupancy[AgeCategoryClassification.Child]?.count === 0;

                return {
                    adultCount: areBothAgeCategoriesZero
                        ? DEFAULT_ADULT_COUNT
                        : occupancy[AgeCategoryClassification.Adult]?.count,
                    childCount: areBothAgeCategoriesZero
                        ? DEFAULT_CHILD_COUNT
                        : occupancy[AgeCategoryClassification.Child]?.count,
                };
            }
        );
};

export function getDaysInterval(startDateOffset: $TSFixMe, endDateOffset: $TSFixMe) {
    return isNil(startDateOffset) || isNil(endDateOffset)
        ? DEFAULT_DAYS_INTERVAL
        : Math.max(0, endDateOffset - startDateOffset);
}

export const getHotelIds = (enterprises: Enterprise[]): string[] | null =>
    enterprises.length === 0 ? null : enterprises.map((enterprise) => enterprise.id);

export function getField(object: $TSFixMe, key: $TSFixMe, otherwise = null) {
    const value = object[key];
    return isEmpty(value) ? otherwise : value;
}

export function getBooleanField(object: $TSFixMe, key: $TSFixMe, otherwise = null) {
    const value = object[key];
    return isBoolean(value) ? value : otherwise;
}

export function getNumberField(object: $TSFixMe, key: $TSFixMe, otherwise = null) {
    const value = object[key];
    return isNumber(value) ? value : otherwise;
}
